import React, { useState, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import { Box, Menu } from "@mui/material";

import TwoColumnContainer from "components/Container/TwoColumnContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

import icn_filter_hover from "assets/images/icons/icn_filter.svg";

const BtnIcon = styled(Box)(() => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0px 8px",
}));

const EMPTY_FILTER_VALUE = "__empty_filter_value__";

const StyledMenu = styled(
  (props) => (
    <Menu
      elevation={0}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      {...props}
    />
  ),
  { shouldForwardProp: (prop) => prop !== "maxWidth" }
)(({ theme }) => ({
  "& .MuiPaper-root": {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(4),
    borderRadius: theme.shape.customBorderRadius[3],
    backgroundColor: theme.palette.primary.white,
    height: "auto",
    border: `1px solid ${theme.palette.primary.whiteSecound}`,
    overflow: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-12px",
      right: "34px",
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderBottom: `12px solid ${theme.palette.primary.white}`,
      filter:
        "drop-shadow(-2px -1px 2px rgba(0, 0, 0, 0.05)) drop-shadow(2px -1px 2px rgba(0, 0, 0, 0.05))",
      zIndex: 1,
    },
  },
}));

const ColumnFilterToggle = ({ table, filterData = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempFilters, setTempFilters] = useState({});

  function handleClose() {
    setAnchorEl(null);
  }

  function renderCellLabel(row, column) {
    try {
      const cellRenderer = column.columnDef.cell;
      if (!cellRenderer) return String(row.getValue(column.id) ?? "");
      const result = cellRenderer({
        row,
        getValue: () => row.getValue(column.id),
        table,
      });
      if (typeof result === "string" || typeof result === "number") return String(result);
      return String(result?.props?.children ?? row.getValue(column.id) ?? "");
    } catch {
      return String(row.getValue(column.id) ?? "");
    }
  };

  const filterOptions = useMemo(() => {
    const result = {};
    filterData.forEach((columnId) => {
      const column = table.getColumn(columnId);
      const rows = table.getPreFilteredRowModel().flatRows;

      const list = rows.map((row) => {
        const rawValue = row.getValue(columnId);
        const isEmptyValue = rawValue === null || rawValue === undefined || rawValue === "";

        return {
          value: isEmptyValue ? EMPTY_FILTER_VALUE : rawValue,
          label: isEmptyValue ? "-" : renderCellLabel(row, column),
          stringValue: isEmptyValue ? EMPTY_FILTER_VALUE : String(rawValue),
        };
      });

      result[columnId] = Array.from(
        new Map(list.map((item) => [item.stringValue, item])).values()
      );
    });
    return result;
  }, [table?.options?.data, filterData]);

  useEffect(() => {
    setTempFilters({});
  }, [table]);

  function handleChange(columnId, stringValue) {
    setTempFilters((prev) => ({ ...prev, [columnId]: stringValue }));
  };

  function handleClear() {
    setTempFilters({});
    filterData.forEach((col) => table.getColumn(col)?.setFilterValue(undefined));
  };

  function handleApply() {
    filterData.forEach((columnId) => {
      const selected = tempFilters[columnId];
      table.getColumn(columnId).setFilterValue(selected)
    });
    handleClose();
  };

  return (
    <>
      <BtnIcon onClick={(e) => setAnchorEl(e.currentTarget)}>
        <img src={icn_filter_hover} alt="filter" />
      </BtnIcon>

      <StyledMenu open={Boolean(anchorEl)} onClose={handleClose} anchorEl={anchorEl}>
        {filterData.map((columnId) => {
          const column = table.getColumn(columnId);
          const label = column.columnDef.header ?? column.id;

          return (
            <Box key={columnId} sx={{ width: 450, margin: "30px 25px" }}>
              <TwoColumnContainer label={label}>
                <MenuSelect
                  value={tempFilters[columnId] ?? table.getColumn(columnId)?.getFilterValue() ?? ""}
                  onChange={(opt) => handleChange(columnId, opt.stringValue)}
                  options={filterOptions[columnId]}
                  placeholder="Select"
                />
              </TwoColumnContainer>
            </Box>
          );
        })}

        <Box display="flex" justifyContent="center" gap={2} mt={1.5}>
          <OutlineButton width="96px" height="32px" onClick={handleClear}>
            Clear
          </OutlineButton>
          <SolidButton width="96px" height="32px" onClick={handleApply}>
            Apply
          </SolidButton>
        </Box>
      </StyledMenu>
    </>
  );
};

export default ColumnFilterToggle;
